import {
  Box,
  Button,
  Typography,
  TextField,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";


const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 4;

const HotelAdd_WPayment_2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { saveStep, fetchDraft, hotelData, loading, } = useHotel();
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    streetAddress: "",
    unit: "",
    city: "",
    region: "",
    postalCode: "",
  });


  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (hotelData.billingAddress) {
      setFormData(hotelData.billingAddress);
    }
  }, [hotelData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);

      const billingAddress = {
        streetAddress: formData.streetAddress,
        unit: formData.unit,
        city: formData.city,
        region: formData.region,
        postalCode: formData.postalCode,
      };

      // Save additional services step
      await saveStep("billingAddress", billingAddress);

      // Navigate to next step
      navigate("/hotel/add/payment-review/" + id);
    } catch (err) {
      console.error("Error saving additional services:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      const billingAddress = {
        streetAddress: formData.streetAddress,
        unit: formData.unit,
        city: formData.city,
        region: formData.region,
        postalCode: formData.postalCode,
      };

      // Save additional services step
      await saveStep("billingAddress", billingAddress);

      // Navigate to next step
      navigate("/hotel/onboarding/");
    } catch (err) {
      console.error("Error saving additional services:", err);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const labelSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 };

  const isNextDisabled =
    !formData.streetAddress || !formData.city || !formData.region || !formData.postalCode;

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 1, display: "flex", alignItems: "center", gap: 1}}>
            {t('PaymentStep2Title')}
            <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
              {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
            </Box>
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#666", mb: 4 }}>
            {t('PaymentStep2Desc')}
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
              <Box>
                <Typography sx={labelSx}>
                  {t('StreetAddressLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>{t('UnitLabel')}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                    {t('OptionalLabel')}
                  </Box>
                </Typography>

                <TextField
                  fullWidth
                  value={formData.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexDirection: isMobile ? "column" : "row" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={labelSx}>
                    {t('CityLabel')}<span style={{ color: "red" }}> *</span>

                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={labelSx}>
                    {t('RegionLabel')}<span style={{ color: "red" }}> *</span>

                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.region}
                    onChange={(e) => handleInputChange("region", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={labelSx}>
                  {t('PostalCodeLabel')}<span style={{ color: "red" }}> *</span>

                </Typography>
                <TextField
                  fullWidth
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange("postalCode", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/payment-1/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_WPayment_2;
